/**
 * nameNormalization.ts
 *
 * Single Source of Truth for standard name & alias normalization.
 * Used on both server and client to match item names against standard dictionary.
 */

/**
 * Normalizes a name string for alias matching.
 * - Chuyển chữ thường, không dấu (bỏ diacritics tiếng Việt).
 * - Bỏ ký tự phân cách (-, _, /, dấu câu).
 * - Ghép số và đơn vị: '5 ly' -> '5ly', '3 ly' -> '3ly', '5 lớp' -> '5lop', '5 L' -> '5l'.
 * - Bỏ khoảng trắng thừa (collapse multiple spaces, trim).
 */
export function normalizeAlias(input: string | null | undefined): string {
  if (!input) return ''
  let s = input
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'd')
    .toLowerCase()

  // Replace punctuation/separators with spaces
  s = s.replace(/[-_/\\.,;:()+]/g, ' ')

  // Merge numbers and common units (e.g. 5 ly -> 5ly, 5 mm -> 5mm, 5 lop -> 5lop)
  s = s.replace(/(\d+)\s*(ly|mm|cm|m|lop|l|lbs|kg|g|eo|xeo)\b/gi, '$1$2')

  // Collapse multiple spaces and trim
  s = s.replace(/\s+/g, ' ').trim()

  return s
}

/**
 * Strips all spaces from normalized alias for compact/fuzzy matching (e.g. 'bulong' vs 'bu long').
 */
export function normalizeAliasCompact(input: string | null | undefined): string {
  return normalizeAlias(input).replace(/\s+/g, '')
}

export interface StandardNameLookupItem {
  id: string
  standard_name: string
  default_uom?: string | null
  aliases?: Array<{
    alias: string
    alias_normalized?: string | null
    confidence?: string | null
  }>
}

export interface MatchStandardNameResult {
  proposed_name: string
  standard_name_id: string | null
  confidence: 'CONFIDENT' | 'NEEDS_REVIEW' | 'UNKNOWN'
  reason: string
}

/**
 * Matches a raw item name against standard dictionary.
 */
export function matchStandardName(
  rawName: string | null | undefined,
  dictionary: StandardNameLookupItem[] = [],
): MatchStandardNameResult {
  const raw = String(rawName ?? '').trim()
  if (!raw) {
    return {
      proposed_name: '',
      standard_name_id: null,
      confidence: 'UNKNOWN',
      reason: 'Tên vật tư rỗng',
    }
  }

  const norm = normalizeAlias(raw)
  const compact = normalizeAliasCompact(raw)

  // 1. Exact match map
  for (const item of dictionary) {
    if (normalizeAlias(item.standard_name) === norm) {
      return {
        proposed_name: item.standard_name,
        standard_name_id: item.id,
        confidence: 'CONFIDENT',
        reason: 'Khớp chính xác tên chuẩn trong từ điển',
      }
    }

    if (item.aliases) {
      for (const a of item.aliases) {
        const aNorm = a.alias_normalized || normalizeAlias(a.alias)
        if (aNorm === norm) {
          const conf = (a.confidence as 'CONFIDENT' | 'NEEDS_REVIEW') || 'CONFIDENT'
          return {
            proposed_name: item.standard_name,
            standard_name_id: item.id,
            confidence: conf,
            reason: 'Khớp chính xác bí danh trong từ điển',
          }
        }
      }
    }
  }

  // 2. Compact match (e.g. "bulong" vs "bu long")
  for (const item of dictionary) {
    if (normalizeAliasCompact(item.standard_name) === compact) {
      return {
        proposed_name: item.standard_name,
        standard_name_id: item.id,
        confidence: 'CONFIDENT',
        reason: 'Khớp bí danh dạng viết liền trong từ điển',
      }
    }

    if (item.aliases) {
      for (const a of item.aliases) {
        if (normalizeAliasCompact(a.alias) === compact) {
          return {
            proposed_name: item.standard_name,
            standard_name_id: item.id,
            confidence: 'CONFIDENT',
            reason: 'Khớp bí danh dạng viết liền trong từ điển',
          }
        }
      }
    }
  }

  // 3. Partial or prefix match for root categories
  for (const item of dictionary) {
    const stdNorm = normalizeAlias(item.standard_name)
    if (stdNorm.length >= 3 && norm.startsWith(stdNorm + ' ')) {
      return {
        proposed_name: item.standard_name,
        standard_name_id: item.id,
        confidence: 'NEEDS_REVIEW',
        reason: `Khớp theo tiền tố '${item.standard_name}' nhưng có quy cách đi kèm trong tên`,
      }
    }

    if (item.aliases) {
      for (const a of item.aliases) {
        const aNorm = a.alias_normalized || normalizeAlias(a.alias)
        if (aNorm.length >= 3 && norm.startsWith(aNorm + ' ')) {
          return {
            proposed_name: item.standard_name,
            standard_name_id: item.id,
            confidence: 'NEEDS_REVIEW',
            reason: `Khớp theo tiền tố bí danh '${a.alias}' nhưng có quy cách đi kèm trong tên`,
          }
        }
      }
    }
  }

  // 4. Unknown
  return {
    proposed_name: raw,
    standard_name_id: null,
    confidence: 'UNKNOWN',
    reason: 'Chưa có trong từ điển tên chuẩn',
  }
}
